import axios from 'axios';
import {
  UnidadNegocio,
  UnidadNegocioDetail,
  UnidadNegocioCreate,
  Area,
  AreaDetail,
  Servicio,
  ServicioCreate,
  Personal,
  PersonalCreate,
  DashboardEstadisticas,
  FiltrosServicio,
  FiltrosPersonal,
  Proyecto,
  ProyectoCreate,
  ProyectoUpdate
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://readyhubapi.azurewebsites.net/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Variable global para el token y promesa para esperar por él
let authToken: string | null = null;
let tokenPromise: Promise<string | null> | null = null;
let tokenResolve: ((token: string | null) => void) | null = null;

// Función para establecer el token
export const setAuthToken = (token: string | null) => {
  authToken = token;
  
  // Resolver la promesa si existe
  if (tokenResolve) {
    tokenResolve(token);
    tokenResolve = null;
    tokenPromise = null;
  }
};

// Función para esperar por el token
const waitForToken = (): Promise<string | null> => {
  if (authToken) {
    return Promise.resolve(authToken);
  }
  
  if (!tokenPromise) {
    tokenPromise = new Promise((resolve) => {
      tokenResolve = resolve;
    });
  }
  
  return tokenPromise;
};

// Interceptor para agregar token Bearer
api.interceptors.request.use(
  async (config) => {
    // Esperar por el token (con timeout de 2 segundos)
    const token = await Promise.race([
      waitForToken(),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), 2000))
    ]);
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// REINTENTOS DESHABILITADOS TEMPORALMENTE - CAUSA BUCLE INFINITO
// Sistema de reintentos
// const MAX_RETRIES = 3;
// const RETRY_DELAY = 1000; // 1 segundo

// const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Interceptor para manejo de errores SIN REINTENTOS
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error (NO RETRY):', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.message
    });
    return Promise.reject(error);
  }
);

export class ApiService {
  // Health Check - Sin autenticación para testing
  static async healthCheck(): Promise<{ message: string; timestamp: string }> {
    const response = await api.get('/dashboard/health');
    return response.data;
  }

  static async diagnostics(): Promise<any> {
    const response = await api.get('/dashboard/diagnostics');
    return response.data;
  }

  // Areas
  static async getAreas(): Promise<Area[]> {
    const response = await api.get('/areas');
    return response.data;
  }

  static async getAreaById(id: number): Promise<AreaDetail> {
    const response = await api.get(`/areas/${id}`);
    return response.data;
  }

  static async createArea(area: { nombre: string; descripcion: string }): Promise<Area> {
    const response = await api.post('/areas', area);
    return response.data;
  }

  static async updateArea(id: number, area: { nombre: string; descripcion: string; activa: boolean }): Promise<Area> {
    const response = await api.put(`/areas/${id}`, area);
    return response.data;
  }

  static async deleteArea(id: number): Promise<void> {
    await api.delete(`/areas/${id}`);
  }

  static async getServiciosByArea(areaId: number): Promise<Servicio[]> {
    const response = await api.get(`/areas/${areaId}/servicios`);
    return response.data;
  }

  static async getPersonalByArea(areaId: number): Promise<Personal[]> {
    const response = await api.get(`/areas/${areaId}/personal`);
    return response.data;
  }

  // Servicios
  static async getServicios(filtros?: FiltrosServicio): Promise<Servicio[]> {
    const params = new URLSearchParams();
    
    if (filtros?.categoria) params.append('categoria', filtros.categoria);
    if (filtros?.areaId) params.append('areaId', filtros.areaId.toString());
    if (filtros?.page) params.append('page', filtros.page.toString());
    if (filtros?.pageSize) params.append('pageSize', filtros.pageSize.toString());

    const response = await api.get(`/servicios?${params.toString()}`);
    return response.data;
  }

  static async getServicioById(id: number): Promise<Servicio> {
    const response = await api.get(`/servicios/${id}`);
    return response.data;
  }

  static async getEquipoByServicio(servicioId: number): Promise<Personal[]> {
    const response = await api.get(`/servicios/${servicioId}/equipo`);
    return response.data;
  }

  static async createServicio(servicio: ServicioCreate): Promise<Servicio> {
    const response = await api.post('/servicios', servicio);
    return response.data;
  }

  static async updateServicio(id: number, servicio: any): Promise<Servicio> {
    const response = await api.put(`/servicios/${id}`, servicio);
    return response.data;
  }

  static async deleteServicio(id: number): Promise<void> {
    await api.delete(`/servicios/${id}`);
  }

  // Personal
  static async getPersonal(filtros?: FiltrosPersonal): Promise<Personal[]> {
    const params = new URLSearchParams();
    
    if (filtros?.areaId) params.append('areaId', filtros.areaId.toString());
    if (filtros?.disponible !== undefined) params.append('disponible', filtros.disponible.toString());

    const response = await api.get(`/personal?${params.toString()}`);
    return response.data;
  }

  static async getPersonalById(id: number): Promise<Personal> {
    const response = await api.get(`/personal/${id}`);
    return response.data;
  }

  static async getPersonalByEmail(email: string): Promise<Personal> {
    const response = await api.get(`/personal/by-email/${encodeURIComponent(email)}`);
    return response.data;
  }

  static async createPersonal(personal: PersonalCreate): Promise<Personal> {
    const response = await api.post('/personal', personal);
    return response.data;
  }

  static async updatePersonal(id: number, personal: any): Promise<Personal> {
    const response = await api.put(`/personal/${id}`, personal);
    return response.data;
  }

  static async updatePersonalProfile(id: number, profileData: Partial<Personal>): Promise<Personal> {
    const response = await api.put(`/personal/${id}/profile`, profileData);
    return response.data;
  }

  static async deletePersonal(id: number): Promise<void> {
    await api.delete(`/personal/${id}`);
  }

  // Dashboard
  static async getDashboardEstadisticas(): Promise<DashboardEstadisticas> {
    const response = await api.get('/dashboard/estadisticas');
    return response.data;
  }

  // Reportes
  static async getReportes(activo?: boolean): Promise<any[]> {
    const params = new URLSearchParams();
    if (activo !== undefined) params.append('activo', activo.toString());
    
    const response = await api.get(`/reportes?${params.toString()}`);
    return response.data;
  }

  static async getReporteById(id: number): Promise<any> {
    const response = await api.get(`/reportes/${id}`);
    return response.data;
  }

  static async getReportesByUsuario(userRole: string): Promise<any[]> {
    const response = await api.get(`/reportes/usuario/${userRole}`);
    return response.data;
  }

  static async createReporte(reporte: any): Promise<any> {
    const response = await api.post('/reportes', reporte);
    return response.data;
  }

  static async updateReporte(id: number, reporte: any): Promise<any> {
    const response = await api.put(`/reportes/${id}`, reporte);
    return response.data;
  }

  static async deleteReporte(id: number): Promise<void> {
    await api.delete(`/reportes/${id}`);
  }

  // Unidades de Negocio
  static async getUnidadesNegocio(): Promise<UnidadNegocio[]> {
    const response = await api.get('/unidadesnegocio');
    return response.data;
  }

  static async getUnidadNegocioById(id: number): Promise<UnidadNegocioDetail> {
    const response = await api.get(`/unidadesnegocio/${id}`);
    return response.data;
  }

  static async getAreasByUnidadNegocio(unidadNegocioId: number): Promise<Area[]> {
    const response = await api.get(`/unidadesnegocio/${unidadNegocioId}/areas`);
    return response.data;
  }

  static async createUnidadNegocio(unidadNegocio: UnidadNegocioCreate): Promise<UnidadNegocio> {
    const response = await api.post('/unidadesnegocio', unidadNegocio);
    return response.data;
  }

  static async updateUnidadNegocio(id: number, unidadNegocio: UnidadNegocioCreate): Promise<UnidadNegocio> {
    const response = await api.put(`/unidadesnegocio/${id}`, unidadNegocio);
    return response.data;
  }

  static async deleteUnidadNegocio(id: number): Promise<void> {
    await api.delete(`/unidadesnegocio/${id}`);
  }

  // Proyectos
  static async getProyectos(areaId?: number, estado?: string): Promise<Proyecto[]> {
    const params = new URLSearchParams();
    if (areaId) params.append('areaId', areaId.toString());
    if (estado) params.append('estado', estado);

    const response = await api.get(`/proyectos?${params.toString()}`);
    return response.data;
  }

  static async getProyectoById(id: number): Promise<Proyecto> {
    const response = await api.get(`/proyectos/${id}`);
    return response.data;
  }

  static async getProyectosByArea(areaId: number): Promise<Proyecto[]> {
    const response = await api.get(`/proyectos/area/${areaId}`);
    return response.data;
  }

  static async createProyecto(proyecto: ProyectoCreate): Promise<Proyecto> {
    const response = await api.post('/proyectos', proyecto);
    return response.data;
  }

  static async updateProyecto(id: number, proyecto: ProyectoUpdate): Promise<Proyecto> {
    const response = await api.put(`/proyectos/${id}`, proyecto);
    return response.data;
  }

  static async deleteProyecto(id: number): Promise<void> {
    await api.delete(`/proyectos/${id}`);
  }

  static async updateServicioUsage(proyectoId: number, servicioId: number, cantidadUsos: number): Promise<void> {
    await api.patch(`/proyectos/${proyectoId}/servicios/${servicioId}/usage`, cantidadUsos);
  }
}

export default ApiService;
